import { createTheme } from "@material-ui/core/styles";
import {
  AEROS,
  buildAerosPalette,
  aerosGradient,
  contrastTextFor,
  mixHex,
} from "./aerosTokens";

export const createAerosTheme = (options, locale) => {
  const {
    mode,
    primaryColor,
    appLogoLight,
    appLogoDark,
    appLogoFavicon,
    appName,
    calculatedLogoLight,
    calculatedLogoDark,
  } = options;

  const p = buildAerosPalette(mode);
  const primary = primaryColor || AEROS.defaultPrimary;
  const gradient = aerosGradient(primary);
  // O botão primário usa um gradiente que termina no ciano (AEROS.cyan); o
  // texto precisa ter contraste nas duas pontas, não só na cor "primary" pura.
  // Ratio 0.7 (em vez do ponto médio 0.5): com a primária padrão #A044FF a
  // mistura no meio exato (#618CF7, luminância ~0.28) ainda fica abaixo do
  // limiar de 0.35 e devolveria texto claro — que dá 1.62:1 na ponta ciano
  // (ilegível). Pesar mais para o lado do ciano cruza o limiar e resulta em
  // texto escuro (#07080D), que mede 4.54:1 na ponta primary e 11.07:1 na
  // ponta cyan — a decisão que o spec manda para o gradiente padrão.
  const onPrimary = contrastTextFor(mixHex(primary, AEROS.cyan, 0.7));
  const isLight = mode === "light";

  return createTheme(
    {
      scrollbarStyles: {
        "&::-webkit-scrollbar": { width: "8px", height: "8px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isLight ? "rgba(15,20,40,0.2)" : "rgba(255,255,255,0.14)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      },
      scrollbarStylesSoft: {
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isLight ? "rgba(15,20,40,0.12)" : "rgba(255,255,255,0.1)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      },
      palette: {
        type: mode,
        primary: {
          main: primary,
          light: mixHex(primary, "#FFFFFF", 0.25),
          dark: mixHex(primary, "#000000", 0.2),
          contrastText: onPrimary,
        },
        secondary: { main: AEROS.cyan, contrastText: "#07080D" },
        success: { main: AEROS.success },
        warning: { main: AEROS.warning },
        error: { main: AEROS.error },
        info: { main: AEROS.info },
        background: { default: p.background, paper: p.surface },
        text: { primary: p.textPrimary, secondary: p.textSecondary },
        divider: p.border,
        // ---- chaves customizadas preservadas (valores Aeros) ----
        textPrimary: primary,
        borderPrimary: primary,
        dark: { main: isLight ? "#171A23" : "#F1F2F6" },
        light: { main: isLight ? "#F1F2F6" : "#171A23" },
        fontColor: primary,
        tabHeaderBackground: p.surface2,
        optionsBackground: p.surface,
        fancyBackground: p.background,
        total: p.surface,
        messageIcons: p.textSecondary,
        inputBackground: p.surface,
        barraSuperior: p.surface,
        // ---- novos tokens Aeros ----
        aeros: p,
        aerosGradient: gradient,
      },
      typography: {
        fontFamily: ['Inter', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(","),
        // Display: Space Grotesk carrega a personalidade high-tech nos títulos;
        // corpo/tabelas seguem em Inter pela legibilidade a 13.5px.
        h1: { fontFamily: AEROS.fontDisplay, fontWeight: 700, letterSpacing: "-0.02em" },
        h2: { fontFamily: AEROS.fontDisplay, fontWeight: 700, letterSpacing: "-0.02em" },
        h3: { fontFamily: AEROS.fontDisplay, fontWeight: 700, letterSpacing: "-0.02em" },
        h4: { fontFamily: AEROS.fontDisplay, fontWeight: 700, letterSpacing: "-0.02em" },
        h5: { fontFamily: AEROS.fontDisplay, fontWeight: 600, letterSpacing: "-0.01em" },
        h6: { fontFamily: AEROS.fontDisplay, fontWeight: 600, letterSpacing: "-0.01em" },
        button: { fontWeight: 600, textTransform: "none", letterSpacing: "0" },
      },
      shape: { borderRadius: 10 },
      overrides: {
        MuiCssBaseline: {
          "@global": { body: { backgroundColor: p.background } },
        },
        MuiButton: {
          root: {
            borderRadius: 10,
            textTransform: "none",
            fontWeight: 600,
            transition: "all 0.2s ease",
          },
          containedPrimary: {
            background: gradient,
            color: onPrimary,
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": {
              // Gradiente "clareado" — mistura cada ponta com branco a 20%,
              // derivado da primária dinâmica (whitelabel). Ratio escolhido
              // por comparação canal-a-canal contra o exemplo do DS
              // (#B366FF→#4ADEF5 para a primária padrão #A044FF): 0.20
              // reproduz exatamente o canal R do stop roxo (0xB3) e fica
              // mais próximo em todos os outros canais que 0.15. Resultado
              // para a primária padrão: #B369FF→#4EDCF1.
              background: `linear-gradient(135deg, ${mixHex(primary, "#FFFFFF", 0.2)}, ${mixHex(AEROS.cyan, "#FFFFFF", 0.2)})`,
              boxShadow: "0 4px 16px rgba(160,68,255,0.4)",
            },
            "&.Mui-disabled": {
              background: isLight ? "rgba(15,20,40,0.06)" : "rgba(255,255,255,0.06)",
              color: p.textTertiary,
            },
          },
          outlined: {
            borderColor: p.borderStrong,
            "&:hover": {
              borderColor: "rgba(34,211,238,0.5)",
              color: AEROS.cyan,
              backgroundColor: "rgba(34,211,238,0.06)",
            },
            "&.Mui-disabled": {
              borderColor: isLight ? "rgba(15,20,40,0.08)" : "rgba(255,255,255,0.08)",
              color: p.textTertiary,
            },
          },
          text: {
            color: AEROS.cyan,
            "&:hover": { color: primary, backgroundColor: "transparent" },
            "&.Mui-disabled": { color: p.textTertiary },
          },
          contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
        },
        MuiContainer: {
          // manter EXATAMENTE os valores atuais de App.js:179-225 (largura total)
          root: {
            paddingLeft: "0 !important",
            paddingRight: "0 !important",
            maxWidth: "none !important",
            width: "100% !important",
          },
          maxWidthLg: { maxWidth: "none !important" },
          maxWidthMd: { maxWidth: "none !important" },
          maxWidthSm: { maxWidth: "none !important" },
          maxWidthXl: { maxWidth: "none !important" },
          maxWidthXs: { maxWidth: "none !important" },
        },
        MuiPaper: {
          root: {
            backgroundImage: "none",
            marginLeft: 0,
            marginRight: 0,
            width: "100%",
            border: `1px solid ${p.border}`,
          },
          rounded: { borderRadius: 14 },
          elevation1: { boxShadow: "none" },
          elevation2: { boxShadow: "none" },
          elevation3: { boxShadow: isLight ? "0 4px 12px rgba(15,20,40,0.08)" : "0 8px 24px rgba(0,0,0,0.4)" },
        },
        MuiMenu: {
          paper: {
            width: "auto !important",
            maxWidth: "300px !important",
            minWidth: "180px !important",
            border: isLight ? "1px solid rgba(15,20,40,0.12)" : "1px solid rgba(255,255,255,0.12)",
            backgroundColor: p.surface2,
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          },
        },
        MuiPopover: {
          paper: {
            width: "auto !important",
            maxWidth: "300px !important",
            minWidth: "auto !important",
            backgroundColor: p.surface2,
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            border: isLight ? "1px solid rgba(15,20,40,0.12)" : "1px solid rgba(255,255,255,0.12)",
          },
        },
        MuiMenuItem: {
          root: {
            borderRadius: 8,
            "&:hover": { backgroundColor: "rgba(160,68,255,0.12)" },
            "&.Mui-selected": {
              backgroundColor: "rgba(160,68,255,0.12)",
              "&:hover": { backgroundColor: "rgba(160,68,255,0.12)" },
            },
          },
        },
        MuiTextField: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 10,
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: p.borderStrong },
            },
          },
        },
        MuiOutlinedInput: {
          root: {
            "&.Mui-disabled": {
              backgroundColor: isLight ? "rgba(15,20,40,0.03)" : "rgba(255,255,255,0.03)",
            },
            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: isLight ? "rgba(15,20,40,0.05)" : "rgba(255,255,255,0.05)",
            },
            // Vivia só em MuiTextField.root > "& .MuiOutlinedInput-root", o que
            // deixava Select/OutlinedInput usados fora de <TextField> (ex.:
            // <Select variant="outlined">) sem o anel de foco/erro ciano do DS.
            // Movido para cá para cobrir qualquer OutlinedInput, com ou sem
            // TextField por cima.
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: AEROS.cyan,
              borderWidth: 1,
            },
            "&.Mui-focused": {
              boxShadow: "0 0 0 3px rgba(34,211,238,0.15)",
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: AEROS.error,
            },
            "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: AEROS.error,
            },
            "&.Mui-error.Mui-focused": {
              boxShadow: "0 0 0 3px rgba(248,113,113,0.15)",
            },
          },
          notchedOutline: { borderColor: p.border },
          input: {
            padding: "10px 14px",
            "&::placeholder": { color: p.textTertiary, opacity: 1 },
          },
        },
        MuiFormHelperText: {
          root: {
            fontSize: 11.5,
            "&$error": { color: AEROS.error },
          },
          contained: { marginLeft: 0, marginRight: 0 },
        },
        MuiSwitch: {
          switchBase: {
            color: p.textSecondary,
            "&.Mui-checked": {
              color: "#07080D",
              "& + .MuiSwitch-track": {
                backgroundColor: "transparent",
                backgroundImage: gradient,
                opacity: 1,
              },
            },
          },
          track: {
            backgroundColor: isLight ? "rgba(15,20,40,0.12)" : "rgba(255,255,255,0.12)",
            opacity: 1,
          },
          thumb: { boxShadow: "none" },
        },
        MuiCheckbox: {
          root: {
            color: p.textSecondary,
            "&.Mui-checked": { color: primary },
          },
        },
        MuiRadio: {
          root: {
            color: p.textSecondary,
            "&.Mui-checked": { color: AEROS.cyan },
          },
        },
        MuiTableCell: {
          root: {
            borderBottom: isLight ? "1px solid rgba(15,20,40,0.05)" : "1px solid rgba(255,255,255,0.05)",
            fontSize: 13.5,
            fontVariantNumeric: "tabular-nums",
          },
          head: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: p.textTertiary,
            backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
          },
        },
        MuiTableRow: {
          root: { "&:hover": { backgroundColor: "rgba(160,68,255,0.04)" } },
        },
        MuiChip: {
          root: { borderRadius: 99, fontWeight: 600, fontSize: 12 },
        },
        MuiTab: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            "&.Mui-selected": { color: p.textPrimary, fontWeight: 700 },
          },
          // MUI v4 emite .MuiTab-textColorPrimary/.MuiTab-textColorSecondary
          // DEPOIS de .MuiTab-root e empata em especificidade — sem essas
          // duas chaves, toda <Tabs textColor="primary|secondary"> (a maioria
          // das telas) sobrescreve a cor de "&.Mui-selected" do root e a aba
          // selecionada volta a ficar roxa (primary) em vez de branca.
          textColorPrimary: { "&.Mui-selected": { color: p.textPrimary } },
          textColorSecondary: { "&.Mui-selected": { color: p.textPrimary } },
        },
        MuiTabs: { indicator: { background: gradient, height: 3, borderRadius: 3 } },
        MuiDialog: {
          paper: {
            border: isLight ? "1px solid rgba(15,20,40,0.12)" : "1px solid rgba(255,255,255,0.12)",
            backgroundColor: p.surface2,
            borderRadius: 16,
            boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(160,68,255,0.15)",
          },
        },
        MuiDialogTitle: {
          root: { "& .MuiTypography-root": { fontSize: 16, fontWeight: 700 } },
        },
        MuiSkeleton: {
          root: { backgroundColor: isLight ? "rgba(15,20,40,0.06)" : "rgba(255,255,255,0.06)" },
        },
        MuiTooltip: {
          tooltip: {
            backgroundColor: p.surface2,
            color: p.textPrimary,
            border: isLight ? "1px solid rgba(15,20,40,0.12)" : "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            fontSize: 12,
          },
        },
        MuiDrawer: { paper: { border: "none", backgroundColor: p.sidebar } },
        MuiAppBar: {
          root: { boxShadow: "none" },
          colorPrimary: {
            backgroundColor: p.surface,
            color: p.textPrimary,
            borderBottom: `1px solid ${p.border}`,
          },
        },
        MuiListSubheader: {
          root: {
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: p.textTertiary,
            backgroundColor: "transparent",
          },
        },
      },
      // ---- chaves de nível do tema preservadas ----
      mode,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      calculatedLogoLight,
      calculatedLogoDark,
    },
    locale
  );
};
